
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import { Check, X, AlertCircle } from 'lucide-react';

interface AccessRequest {
  id: string;
  user_id: string | null;
  email: string | null;
  company_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  notes: string | null;
  profile: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  };
  company: {
    name: string;
  };
}

interface ProfileData {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export function AccessRequestsPanel() {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const [processingId, setProcessingId] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchAccessRequests();
    
    // Subscribe to real-time changes
    const channel = supabase
      .channel('access-requests-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'company_access_requests',
        },
        (payload) => {
          console.log('Access request changed:', payload);
          fetchAccessRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchAccessRequests = async () => {
    try {
      setIsLoading(true);
      // First get the request data
      const { data: requestsData, error: requestsError } = await supabase
        .from('company_access_requests')
        .select(`
          *,
          company:company_id(name)
        `)
        .or('status.is.null,status.eq.pending')
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;
      
      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
        setIsLoading(false);
        return;
      }
      
      // Now fetch profile data for each user_id (excluding nulls)
      const userIds = requestsData
        .map(request => request.user_id)
        .filter((id): id is string => id !== null);
      
      let profilesData = [];
      if (userIds.length > 0) {
        const { data, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .in('id', userIds);
          
        if (profilesError) throw profilesError;
        profilesData = data || [];
      }
      
      // Map the profiles to the requests
      const requestsWithProfiles = requestsData.map(request => {
        // For invitation-based requests, use the email field directly
        if (!request.user_id && request.email) {
          return {
            ...request,
            profile: {
              first_name: null,
              last_name: null,
              email: request.email
            }
          } as AccessRequest;
        }
        
        // For user-initiated requests, look up the profile
        const profile = profilesData?.find(p => p.id === request.user_id);
        return {
          ...request,
          profile: {
            first_name: profile?.first_name || null,
            last_name: profile?.last_name || null,
            email: profile?.email || null
          }
        } as AccessRequest;
      });
      
      setRequests(requestsWithProfiles);
    } catch (error: any) {
      console.error('Error fetching access requests:', error.message);
      toast({
        title: 'Error',
        description: 'Failed to load access requests',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      setProcessingId(requestId);
      
      // Get the request details
      const request = requests.find(r => r.id === requestId);
      if (!request) return;
      
      // Only allow approval of user-initiated requests (not invitations)
      if (!request.user_id) {
        toast({
          title: 'Cannot Approve',
          description: 'This is a pending invitation. The user must accept it via email.',
          variant: 'destructive',
        });
        setProcessingId(null);
        return;
      }
      
      const { error } = await supabase
        .from('company_access_requests')
        .update({
          status: 'approved',
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
          notes: adminNotes[requestId] || request.notes || 'Approved by admin'
        })
        .eq('id', requestId);

      if (error) throw error;

      // Update the local state
      setRequests(requests.filter(request => request.id !== requestId));
      
      // Clear the note for this request
      const newNotes = { ...adminNotes };
      delete newNotes[requestId];
      setAdminNotes(newNotes);
      
      // Force refresh to confirm state and trigger realtime listeners
      fetchAccessRequests();
      
      toast({
        title: 'Request Approved',
        description: 'User has been added to the company',
      });
    } catch (error: any) {
      console.error('Error approving request:', error.message);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve request',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      setProcessingId(requestId);
      
      const request = requests.find(r => r.id === requestId);
      if (!request) return;
      
      // For invitations (no user_id), DELETE the record
      // For user requests (has user_id), UPDATE status to rejected
      if (!request.user_id) {
        // This is an invitation - delete it
        const { error } = await supabase
          .from('company_access_requests')
          .delete()
          .eq('id', requestId);

        if (error) throw error;
      } else {
        // This is a user-initiated request - reject it
        const { error } = await supabase
          .from('company_access_requests')
          .update({
            status: 'rejected',
            updated_at: new Date().toISOString(),
            updated_by: user?.id,
            notes: adminNotes[requestId] || request.notes || 'Rejected by admin'
          })
          .eq('id', requestId);

        if (error) throw error;
      }

      // Update the local state
      setRequests(requests.filter(request => request.id !== requestId));
      
      // Clear the note for this request
      const newNotes = { ...adminNotes };
      delete newNotes[requestId];
      setAdminNotes(newNotes);
      
      // Force refresh to confirm state and trigger realtime listeners
      fetchAccessRequests();
      
      const actionText = request.user_id ? 'Access request' : 'Invitation';
      toast({
        title: `${actionText} ${request.user_id ? 'Rejected' : 'Canceled'}`,
        description: `${actionText} has been ${request.user_id ? 'denied' : 'canceled'}`,
      });
    } catch (error: any) {
      console.error('Error rejecting request:', error.message);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject request',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Access Requests</CardTitle>
        <CardDescription>
          Approve or reject user requests to join companies
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex justify-center items-center p-6">Loading requests...</div>
        ) : requests.length === 0 ? (
          <div className="flex justify-center items-center p-6 text-muted-foreground">
            <AlertCircle className="mr-2 h-4 w-4" />
            No pending access requests
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Admin Note</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    {request.user_id ? (
                      <>
                        <div className="font-medium">
                          {request.profile.first_name} {request.profile.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {request.profile.email}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-medium">
                          {request.profile.email}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <Badge variant="secondary" className="text-xs">Invited by email</Badge>
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{request.company.name}</Badge>
                  </TableCell>
                  <TableCell>
                    {format(new Date(request.created_at), 'PPp')}
                  </TableCell>
                  <TableCell>
                    <Textarea
                      placeholder="Optional notes (visible to admins only)"
                      value={adminNotes[request.id] || request.notes || ''}
                      onChange={(e) => setAdminNotes({ ...adminNotes, [request.id]: e.target.value })}
                      className="h-20 text-sm"
                    />
                  </TableCell>
                  <TableCell>
                    {request.user_id ? (
                      // User-initiated request - admin can approve/reject
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center"
                          onClick={() => handleApproveRequest(request.id)}
                          disabled={!!processingId}
                        >
                          <Check className="mr-1 h-4 w-4 text-green-500" />
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center"
                          onClick={() => handleRejectRequest(request.id)}
                          disabled={!!processingId}
                        >
                          <X className="mr-1 h-4 w-4 text-red-500" />
                          Reject
                        </Button>
                      </div>
                    ) : (
                      // Invitation - can only cancel/reject
                      <div className="flex space-x-2">
                        <Badge variant="secondary" className="text-xs">
                          Pending acceptance
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center"
                          onClick={() => handleRejectRequest(request.id)}
                          disabled={!!processingId}
                        >
                          <X className="mr-1 h-4 w-4 text-red-500" />
                          Cancel Invite
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
